import React, { useEffect, useState, useMemo, useRef } from "react"
import styled from "styled-components"
import { Avatar, Badge, Button, Tooltip } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBars,
  faBell,
  faLeftToBracket,
  faMessage,
} from "@fortawesome/pro-regular-svg-icons"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { screenSizes } from "../styles/GlobalStyles"
import swimHeader from "../images/swimmingly_final_header.png"
import swimHeaderMobile from "../images/swimmingly_final_header_mobile.png"
import { IUser, RoleHelper, useScreenSize, gen, swimminglyApi } from "./utils"
import { useClubSeasonManagementContext } from "./ClubSeasonManagement/sharedState"
import { useHistory } from "react-router-dom"
import moment from "moment-timezone"
import SupportWidgets from "./SupportWidgets"
import { DateFormats } from "./utils"
import { SeasonHelper } from "./ClubSeasonManagement/Season"
import _ from "lodash"
import { useFeatureAccess } from "./FeatureAccessContext"
import { FeatureKey } from "../constants/featureAccess"

const StyledHeader = styled.header`
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1200;

  background-color: var(--primaryblue, #25bfea);
  border-bottom: 1px solid var(--primaryblue, #25bfea);
  border-top: 1px solid var(--primaryblue, #25bfea);

  @media only screen and (max-width: ${screenSizes.small}px) {
    height: auto;
    /* keep fixed on mobile as well */
    background-color: var(--primaryblue, #25bfea);
    border-bottom: 1px solid var(--primaryblue, #25bfea);
    border-top: 1px solid var(--primaryblue, #25bfea);
  }
`

interface IAppHeaderProps {
  setEditUserVisible: ((value: boolean) => void) | null
  setActivityFeedVisible: ((value: boolean) => void) | null
  setClubCommunicationVisible: ((value: boolean) => void) | null
  setSidebarClosed: ((value: boolean) => void) | null
  sidebarClosed: boolean | null
}

export default function AppHeader({
  setEditUserVisible,
  setActivityFeedVisible,
  setClubCommunicationVisible,
  setSidebarClosed,
  sidebarClosed,
}: IAppHeaderProps): JSX.Element {
  const headerRef = useRef<HTMLElement | null>(null)
  const authUser = useStoreState((state: StateMapper<AppDataStore>) => state.authUser)
  const aliasedUser = useStoreState((state: StateMapper<AppDataStore>) => state.aliasedUser)
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const roleHelper = new RoleHelper(user?.role)
  const showActivityFeedBell = Boolean(
    user && (roleHelper.isAnyAdmin() || roleHelper.isGuardian()),
  )
  const impersonateClub = useStoreState((state: StateMapper<AppDataStore>) => state.aliasedClub)
  const { loadUpcomingSeason, upcomingSeason } = useClubSeasonManagementContext()
  const [upcomingSeasonIsActive, setUpcomingSeasonIsActive] = useState<Boolean | undefined>(false)

  useEffect(() => {
    if (roleHelper.isAnyAdmin()) {
      loadUpcomingSeason()
    }
  }, [loadUpcomingSeason, impersonateClub])

  useEffect(() => {
    if (roleHelper.isAnyAdmin()) {
      const seasonStartDate = moment(upcomingSeason?.startDate, DateFormats.default)
      const seasonEndDate = moment(upcomingSeason?.endDate, DateFormats.default)
      const today = moment()
      const localUpcomingSeasonIsActive = today.isBetween(seasonStartDate, seasonEndDate)
      setUpcomingSeasonIsActive((prev) => Boolean(prev) || localUpcomingSeasonIsActive)
    }
  }, [upcomingSeason])

  useEffect(() => {
    if (user?.userId) {
      new SeasonHelper()
        .getClubSeasonsForGuardian(user?.userId)
        .then((clubSeasons) => {
          const today = moment()
          const upcomingClubSeasons = clubSeasons.filter((clubSeason) =>
            today.isBetween(clubSeason.startDate, clubSeason.endDate),
          )
          const localUpcomingSeasonIsActive = !_.isEmpty(upcomingClubSeasons)
          setUpcomingSeasonIsActive((prev) => Boolean(prev) || localUpcomingSeasonIsActive)
        })
    }
  }, [user])

  const history = useHistory()
  const screenSize = useScreenSize()
  const isMobile: boolean = screenSize === "small"

  const [activityFeedUnreadCount, setActivityFeedUnreadCount] = useState<number>(0)
  const [unreadAnnouncements, setUnreadAnnouncements] = useState<number>(0)
  const [stoppingAlias, setStoppingAlias] = useState(false)
  const { isFeatureLocked, openLockedModal } = useFeatureAccess()

  const stopAliasing = async () => {
    if (!aliasedUser || stoppingAlias) {
      return
    }

    setStoppingAlias(true)
    try {
      const response = await swimminglyApi
        .post(gen("/api/updateAliasedUser"))
        .body({ aliasedUserId: null })

      if (response?.status === "error") {
        throw new Error(response?.message || "Unable to stop aliasing right now.")
      }

      if (typeof window !== "undefined") {
        window.location.reload()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setStoppingAlias(false)
    }
  }

  useEffect(() => {
    if (!user?.userId) return

    const loadUnreadAnnouncements = () => {
      const query = `?userId=${encodeURIComponent(String(user.userId))}`
      swimminglyApi
        .get(gen(`/api/messaging/unread-announcement-count${query}`))
        .then((resp: any) => {
          const data: any = (resp as any)?.data ?? resp ?? {}
          const count = typeof data.unreadCount === "number" ? data.unreadCount : 0
          setUnreadAnnouncements(count)
        })
        .catch(() => {
          // best-effort; keep last known count
        })
    }

    loadUnreadAnnouncements()

    const handleRefetch = () => {
      loadUnreadAnnouncements()
    }

    document.addEventListener("swimmingly:clubhouse-announcements:refetch", handleRefetch)
    const intervalId = window.setInterval(loadUnreadAnnouncements, 60000)

    return () => {
      document.removeEventListener("swimmingly:clubhouse-announcements:refetch", handleRefetch)
      window.clearInterval(intervalId)
    }
  }, [user?.userId])

  useEffect(() => {
    if (!user?.userId || !showActivityFeedBell) {
      setActivityFeedUnreadCount(0)
      return
    }

    const loadUnreadCount = () => {
      const query = `?userId=${encodeURIComponent(String(user.userId))}`
      swimminglyApi
        .get(gen(`/api/activity-feed/summary${query}`))
        .then((resp: any) => {
          const data: any = (resp as any)?.data ?? resp ?? {}
          const count =
            typeof data.unreadCount === "number"
              ? data.unreadCount
              : typeof data.summary?.unreadCount === "number"
                ? data.summary.unreadCount
                : 0
          setActivityFeedUnreadCount(count)
        })
        .catch(() => {
          // best-effort; keep last known count
        })
    }

    loadUnreadCount()

    const handleRefetch = () => {
      loadUnreadCount()
    }

    window.addEventListener("swimmingly:activity-feed:refetch", handleRefetch)
    window.addEventListener("websitesPaymentsViewed", handleRefetch)

    const intervalId = window.setInterval(loadUnreadCount, 60000)

    return () => {
      window.removeEventListener("swimmingly:activity-feed:refetch", handleRefetch)
      window.removeEventListener("websitesPaymentsViewed", handleRefetch)
      window.clearInterval(intervalId)
    }
  }, [showActivityFeedBell, user?.userId])

  // compute header height used for the banner / normal header and expose it as a CSS variable
  const mainRowHeightPx = isMobile ? 48 : 44
  const bannerHeightPx = 64

  useEffect(() => {
    // Measure actual rendered header height (including borders) and expose it via CSS var
    if (typeof document !== "undefined" && headerRef.current) {
      const measuredHeight = headerRef.current.offsetHeight
      document.documentElement.style.setProperty("--app-header-height", `${measuredHeight}px`)
    }
  }, [isMobile])

  const isDevEnv = useMemo(() => {
    if (typeof window === "undefined") return false
    const host = window.location.hostname || ""
    return host === "localhost" || host === "127.0.0.1"
  }, [])

  const isStagingEnv = useMemo(() => {
    if (typeof window === "undefined") return false
    const host = window.location.hostname || ""
    return host.includes("swimminglytesting.ninja")
  }, [])

  const makeAvatar = (theUser?: IUser | null): JSX.Element => {
    const common = {
      size: 20,
      style: { backgroundColor: "#101242", cursor: "pointer" as const, display: "inline-flex", alignItems: "center", justifyContent: "center" },
    }

    if (!theUser) {
      return (
        <button
          className="app-header-icon-button"
          aria-label="Open profile"
          onClick={setEditUserVisible ? () => setEditUserVisible(true) : undefined}
        >
          <Avatar {...common} icon={<UserOutlined />} />
        </button>
      )
    }

    let initials = ""
    const nameSplit = theUser.name?.split(" ") || []
    if (nameSplit[0]) initials += nameSplit[0][0]
    if (nameSplit[1]) initials += nameSplit[1][0]

    if (!initials) {
      return (
        <button
          className="app-header-icon-button"
          aria-label="Open profile"
          onClick={setEditUserVisible ? () => setEditUserVisible(true) : undefined}
        >
          <Badge count={1}>
            <Avatar {...common} icon={<UserOutlined />} />
          </Badge>
        </button>
      )
    }

    return (
      <button
        className="app-header-icon-button"
        aria-label="Open profile"
        onClick={setEditUserVisible ? () => setEditUserVisible(true) : undefined}
      >
        <Avatar {...common}>{initials}</Avatar>
      </button>
    )
  }

  const emojiSize: string = "2.5vw"
  const bannerCaption = `${upcomingSeason?.seasonName || "Your season"} - Complete season registration setup to open season for ${impersonateClub?.nameLong || "your club"}.`

  const envPill = useMemo(() => {
    if (isDevEnv) {
      return {
        text: "DEVELOPMENT ENVIRONMENT — LOCALHOST / TEST DATA",
        shortText: "DEV — LOCALHOST / TEST",
        backgroundColor: "#faad14",
        color: "#000000",
      }
    }

    if (isStagingEnv) {
      return {
        text: "STAGING ENVIRONMENT — DATA IS NOT LIVE",
        shortText: "STAGING — NOT LIVE",
        backgroundColor: "#ff4d4f",
        color: "#ffffff",
      }
    }

    return null
  }, [isDevEnv, isStagingEnv])

  const aliasedUserClubName =
    impersonateClub?.nameLong || impersonateClub?.name || "No club"

  return (
    <StyledHeader ref={headerRef}>
      {/* main header row: fixed height and vertically center contents */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: `${mainRowHeightPx}px`, padding: "0 12px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
            flex: isMobile ? "0 1 auto" : "0 1 50%",
          }}
        >
          <img src={isMobile ? swimHeaderMobile : swimHeader} height={isMobile ? 26 : 30} alt="Swimmingly Header" />

          {aliasedUser && authUser && aliasedUser.userId !== authUser.userId ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                backgroundColor: "rgba(16, 18, 66, 0.22)",
                border: "1px solid rgba(255, 255, 255, 0.25)",
                borderRadius: 999,
                padding: isMobile ? "1px 6px" : "2px 8px",
                color: "#ffffff",
                maxWidth: isMobile ? "210px" : "420px",
                minWidth: 0,
                height: isMobile ? 22 : 24,
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1,
                  minWidth: 0,
                }}
              >
                {aliasedUser.name || aliasedUser.email} • {aliasedUserClubName}
              </span>
              <Tooltip title="Stop aliasing">
                <Button
                  size="small"
                  type="text"
                  aria-label="Stop aliasing"
                  onClick={stopAliasing}
                  loading={stoppingAlias}
                  icon={
                    stoppingAlias ? undefined : <FontAwesomeIcon icon={faLeftToBracket} />
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    color: "#ffffff",
                    minWidth: 18,
                    width: 18,
                    height: 18,
                    padding: 0,
                    lineHeight: 1,
                    flex: "0 0 auto",
                  }}
                />
              </Tooltip>
            </div>
          ) : null}
        </div>

        <div style={{ flex: 1, display: "flex", justifyContent: "center", padding: "0 8px", minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flexWrap: isMobile ? "wrap" : "nowrap",
              minWidth: 0,
            }}
          >
            {envPill && (
              <div
                style={{
                  backgroundColor: envPill.backgroundColor,
                  color: envPill.color,
                  fontWeight: 600,
                  padding: "0 8px",
                  fontSize: isMobile ? 9 : 11,
                  lineHeight: 1.1,
                  letterSpacing: 0.5,
                  borderRadius: 999,
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  maxWidth: isMobile ? "80%" : "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "center",
                }}
              >
                {isMobile && envPill.shortText ? envPill.shortText : envPill.text}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: isMobile ? 6 : 12 }}>
          {showActivityFeedBell && (
            <button
              className="app-header-icon-button"
              aria-label="Open activity feed"
              onClick={setActivityFeedVisible ? () => setActivityFeedVisible(true) : undefined}
            >
              {activityFeedUnreadCount > 0 ? (
                <Badge count={activityFeedUnreadCount} size="small" offset={[-2, 2]} style={{ boxShadow: "none" }}>
                  <FontAwesomeIcon
                    icon={faBell}
                    style={{
                      fontSize: 18,
                      width: 20,
                      height: 20,
                      "--fa-primary-color": "#ffffff",
                      "--fa-secondary-color": "#101242",
                      "--fa-secondary-opacity": 0.8,
                    } as any}
                  />
                </Badge>
              ) : (
                <FontAwesomeIcon
                  icon={faBell}
                  style={{
                    fontSize: 18,
                    width: 20,
                    height: 20,
                    "--fa-primary-color": "#ffffff",
                    "--fa-secondary-color": "#101242",
                    "--fa-secondary-opacity": 0.8,
                  } as any}
                />
              )}
            </button>
          )}

          {/* message icon with unread badge */}
          {user && (upcomingSeasonIsActive || isFeatureLocked(FeatureKey.clubCommunication) ? (
            <button
              className="app-header-icon-button"
              aria-label="Open club communications"
              onClick={() => {
                if (isFeatureLocked(FeatureKey.clubCommunication)) {
                  openLockedModal(FeatureKey.clubCommunication)
                } else if (setClubCommunicationVisible) {
                  setClubCommunicationVisible(true)
                }
              }}
            >
              {unreadAnnouncements > 0 ? (
                <Badge count={unreadAnnouncements} size="small" offset={[-2, 2]} style={{ boxShadow: "none" }}>
                  <FontAwesomeIcon
                    icon={faMessage}
                    style={{
                      fontSize: 18,
                      width: 20,
                      height: 20,
                      "--fa-primary-color": "#ffffff",
                      "--fa-secondary-color": "#101242",
                      "--fa-secondary-opacity": 0.8,
                    } as any}
                  />
                </Badge>
              ) : (
                <FontAwesomeIcon
                  icon={faMessage}
                  style={{
                    fontSize: 18,
                    width: 20,
                    height: 20,
                    "--fa-primary-color": "#ffffff",
                    "--fa-secondary-color": "#101242",
                    "--fa-secondary-opacity": 0.8,
                  } as any}
                />
              )}
            </button>
          ) : user.role === 6 ? (
            <Tooltip title="This feature is for paying clubs only. Register with Swimmingly to easily message your whole club!" placement="left" color="#8898aa" mouseEnterDelay={0.5}>
              <span className="app-header-icon-button">
                <FontAwesomeIcon
                  icon={faMessage}
                  style={{
                    fontSize: 18,
                    width: 20,
                    height: 20,
                    "--fa-primary-color": "#EAF9FF",
                    "--fa-secondary-color": "#EAF9FF",
                    "--fa-secondary-opacity": 0.3,
                  } as any}
                />
              </span>
            </Tooltip>
          ) : (user.role === 4 || user.role === 3 || user.role === 2) && !upcomingSeasonIsActive ? (
            <span className="app-header-icon-button">
              <FontAwesomeIcon
                icon={faMessage}
                style={{
                  fontSize: 18,
                  width: 20,
                  height: 20,
                  "--fa-primary-color": "#ffffff",
                  "--fa-secondary-color": "#101242",
                  "--fa-secondary-opacity": 0.4,
                } as any}
              />
            </span>
          ) : null)}

          {makeAvatar(user)}

          <div id="my-widget-container" />

          {isMobile && (
            <button
              className="app-header-icon-button"
              aria-label="Toggle navigation menu"
              aria-controls="mobile-sidebar-drawer"
              aria-expanded={!sidebarClosed}
              onClick={setSidebarClosed !== null && sidebarClosed !== null ? () => setSidebarClosed(!sidebarClosed) : undefined}
            >
              <FontAwesomeIcon icon={faBars} style={{ fontSize: 20, color: "#ffffff" }} />
            </button>
          )}
        </div>
      </div>
      <SupportWidgets />
    </StyledHeader>
  )
}